import React from "react";
import { Link } from "react-router-dom";
import { Download, Filter } from "lucide-react";
import { Card, CardHeader, CardContent, Text } from "@tonic/shared/components/ui";

/**
 * @typedef {Object} RecycleItem
 * @property {*} id
 * @property {*} facility
 * @property {*} dateReceived
 * @property {*} status
 * @property {*} weight
 */

const mockItems = [
  {
    id: "LRPLY00545897",
    facility: "USCA005\nCalifornia, United States",
    dateReceived: "10 Oct 23 at 10:43",
    status: "Sent to Recycler",
    weight: 0.43,
  },
  {
    id: "LRPLY00354861",
    facility: "CAON015\nOntario, United States",
    dateReceived: "10 Oct 23 at 10:42",
    status: "Sent to Recycler",
    weight: 0.85,
  },
  {
    id: "LRPLY00354862",
    facility: "USCA005\nCalifornia, United States",
    dateReceived: "10 Oct 23 at 10:41",
    status: "In Storage",
    weight: 0.62,
  },
  {
    id: "LRPLY00354863",
    facility: "CAON015\nOntario, United States",
    dateReceived: "10 Oct 23 at 10:40",
    status: "Assessment",
    weight: 0.91,
  },
  {
    id: "LRPLY00354864",
    facility: "USCA005\nCalifornia, United States",
    dateReceived: "10 Oct 23 at 10:39",
    status: "Received",
    weight: 0.73,
  },
];

export function RecycleInformationPage() {
  const mockLocations = [
    {
      id: "LOC001",
      name: "San Francisco Center",
      address: "123 Main St, San Francisco, CA",
      acceptedItems: ["Electronics", "Batteries", "Packaging"],
      operatingHours: "Mon-Fri: 9AM-5PM",
      contact: "415-555-0123",
    },
    {
      id: "LOC002",
      name: "Los Angeles Hub",
      address: "456 Oak Ave, Los Angeles, CA",
      acceptedItems: ["Electronics", "Metals", "Plastics"],
      operatingHours: "Mon-Sat: 8AM-6PM",
      contact: "213-555-0456",
    },
    {
      id: "LOC003",
      name: "Seattle Facility",
      address: "789 Pine St, Seattle, WA",
      acceptedItems: ["Electronics", "Glass", "Paper"],
      operatingHours: "Mon-Fri: 8AM-4PM",
      contact: "206-555-0789",
    },
  ];

  const mockGuidelines = [
    {
      id: "GUIDE001",
      category: "Electronics",
      requirements: [
        "Remove batteries",
        "Wipe personal data",
        "Include all accessories",
      ],
      restrictions: ["No damaged items", "No liquid damage"],
    },
    {
      id: "GUIDE002",
      category: "Packaging",
      requirements: [
        "Clean and dry",
        "Flatten boxes",
        "Remove non-recyclable parts",
      ],
      restrictions: ["No food residue", "No mixed materials"],
    },
    {
      id: "GUIDE003",
      category: "Batteries",
      requirements: ["Sort by type", "Tape terminal ends", "Pack separately"],
      restrictions: ["No leaking batteries", "No damaged cells"],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Recycling Information</h1>
        <div className="flex gap-4">
          <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200">
            <Filter className="w-4 h-4" />
            Filter
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Locations */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-medium">Recycling Locations</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {mockLocations.map((location) => (
            <div key={location.id} className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {location.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {location.address}
                  </p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Accepted Items
                  </h4>
                  <ul className="mt-1 text-sm text-gray-900">
                    {location.acceptedItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Operating Hours
                  </h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {location.operatingHours}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Contact</h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {location.contact}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Guidelines */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-medium">Recycling Guidelines</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {mockGuidelines.map((guide) => (
            <div key={guide.id} className="p-6">
              <h3 className="text-lg font-medium text-gray-900">
                {guide.category}
              </h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Requirements
                  </h4>
                  <ul className="mt-2 space-y-2">
                    {guide.requirements.map((req, index) => (
                      <li
                        key={index}
                        className="flex items-center text-sm text-gray-900"
                      >
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Restrictions
                  </h4>
                  <ul className="mt-2 space-y-2">
                    {guide.restrictions.map((restriction, index) => (
                      <li
                        key={index}
                        className="flex items-center text-sm text-gray-900"
                      >
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>
                        {restriction}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
